// import 'firebase/compat/auth';

import { ApolloClient, createHttpLink, from, InMemoryCache, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition, relayStylePagination } from '@apollo/client/utilities';
import { observabilityPrivateGraphUrl } from '@gonfalon/constants/constants';

import { invalidateRefetch } from './gql';

// in dev, proxy to frontend server (`createProxy.ts` forwards to o11y backend)
const privateGraphUri = !__DEV__
  ? (observabilityPrivateGraphUrl() ?? 'https://pri.observability.app.launchdarkly.com')
  : '/observability/private';
const highlightGraph = createHttpLink({
  uri: privateGraphUri,
  credentials: 'include',
});
let splitLink = null;
try {
  const socketUri = !__DEV__
    ? privateGraphUri.replace('http://', 'ws://').replace('https://', 'wss://')
    : '/observability/private/ws';
  const highlightSocket = new WebSocketLink({
    uri: socketUri,
    options: {
      lazy: true,
      reconnect: true,
    },
  });
  splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    highlightSocket,
    highlightGraph,
  );
} catch (error) {
  console.log('Error setting up websocket: ', error);
}

const authLink = setContext((_, { headers }) => // authentication handled by cookie which is sent implicitly
({ headers: { ...headers } }));

const cache = new InMemoryCache({
  typePolicies: {
    WorkspaceAdminRole: {
      keyFields: ['workspaceId', 'admin', ['id']],
    },
    Query: {
      fields: {
        logs: relayStylePagination([
          'project_id',
          'at',
          'direction',
          'params',
          ['query', 'date_range', ['start_date', 'end_date']],
        ]),
        traces: relayStylePagination([
          'project_id',
          'at',
          'direction',
          'params',
          ['query', 'date_range', ['start_date', 'end_date']],
        ]),
        visualization: {
          read(_, { args, toReference }) {
            return toReference({
              __typename: 'Visualization',
              id: args?.id,
            });
          },
        },
      },
    },
  },
});

// Create a link factory that accepts a project ID getter
// This eliminates the global state and race conditions
const createProjectIdLink = (getProjectId?: () => string | undefined) =>
  setContext((_, { headers }) => {
    const projectId = getProjectId?.();

    if (projectId) {
      return {
        headers: {
          ...headers,
          'X-LD-Project-Id': projectId,
        },
      };
    }

    return { headers };
  });

const enableApolloDevTools = process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test';

// Factory to create Apollo client with proper project ID scoping
export const createApolloClient = (getProjectId?: () => string | undefined) => {
  const projectIdLink = createProjectIdLink(getProjectId);

  return new ApolloClient({
    link: from([projectIdLink, authLink.concat(splitLink || highlightGraph)]),
    defaultOptions: {
      mutate: {
        onQueryUpdated: invalidateRefetch,
      },
    },
    cache,
    assumeImmutableResults: true,
    devtools: { enabled: enableApolloDevTools },
  });
};

// Default client for backward compatibility (no project ID)
export const client = createApolloClient();
